import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@store';
import { css } from '@emotion/css';
import { Theme, useTheme, Typography, InputBase, IconButton, Popover, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { FeaturePermission, useFeaturePermission } from '@utils/featurePermissions';
import { useUnreadCount } from '../../chat/store/chatStore';
import {
  BiSolidDashboard,
  BiSolidCar,
  BiSolidMapPin,
  BiSolidUser,
  BiGroup,
  BiCog,
  BiLogOut,
  BiSolidBuildingHouse,
  BiSolidIdCard,
  BiSolidCategory,
  BiSolidNetworkChart,
  BiSolidDirectionRight,
  BiSolidVector,
  BiSolidReceipt as BiSolidReceiptFinance,
  BiSolidCopyAlt,
  BiSolidErrorCircle,
  BiSolidCommentX,
  BiSolidFileFind,
  BiSolidGridAlt,
  BiSolidWallet,
  BiSolidTimeFive,
  BiSolidShield,
  BiSolidTimer,
  BiSolidCommentDetail,
  BiChat,
  BiSolidCompass,
  BiSolidMapAlt,
  BiSolidCalculator,
  BiLink,
  BiSolidBookContent,
  BiSolidAddToQueue,
  BiSolidDollarCircle,
  BiSolidCheckShield,
  BiSolidWrench,
  BiWorld,
  BiSolidPurchaseTag,
  BiNetworkChart,
  BiGitBranch,
  BiSolidBug,
  BiSolidCheckCircle,
  BiSolidCog,
  BiCalendar,
  BiHash,
} from 'react-icons/bi';
import NavItem from './NavItem';
import NavFold from './NavFold';

import hopdriveLogo from '@assets/vectors/logo-banner-light.svg';
import hopdriveLogoCondensed from '@assets/vectors/rabbit.svg';
import { buildIdentity } from '@components/BuildBadge';

// Helper function to check if user has permission for a specific route
const useRoutePermission = (route?: string): boolean => {
  const hasCustomer = useFeaturePermission(FeaturePermission.CUSTOMER);
  const hasDispatching = useFeaturePermission(FeaturePermission.DISPATCHING);
  const hasDispatchingChat = useFeaturePermission(FeaturePermission.DISPATCHING_CHAT);
  const hasDispatchingGeography = useFeaturePermission(FeaturePermission.DISPATCHING_GEOGRAPHY);
  const hasDispatchingRegions = useFeaturePermission(FeaturePermission.DISPATCHING_REGIONS);
  const hasDriver = useFeaturePermission(FeaturePermission.DRIVER);
  const hasDriverCertifications = useFeaturePermission(FeaturePermission.DRIVER_CERTIFICATIONS);
  const hasDriverEquipmentManagement = useFeaturePermission(FeaturePermission.DRIVER_EQUIPMENT_MANAGEMENT);
  const hasDriverOnboarding = useFeaturePermission(FeaturePermission.DRIVER_ONBOARDING);
  const hasDriverPay = useFeaturePermission(FeaturePermission.DRIVER_PAY);
  const hasEventLogs = useFeaturePermission(FeaturePermission.EVENT_LOGS);
  const hasFinance = useFeaturePermission(FeaturePermission.FINANCE);
  const hasHangtags = useFeaturePermission(FeaturePermission.HANGTAGS);
  const hasLearningModule = useFeaturePermission(FeaturePermission.LEARNING_MODULE);
  // hasDriverEquipmentManagement + hasDispatchingChat are already declared
  // above (lines 66/71); only the genuinely-new perms are added here.
  const hasPromos = useFeaturePermission(FeaturePermission.PROMOS);
  const hasOutcomes = useFeaturePermission(FeaturePermission.OUTCOMES);
  const hasRiskAssessment = useFeaturePermission(FeaturePermission.RISK_ASSESSMENT);
  const hasTech = useFeaturePermission(FeaturePermission.TECH);
  const hasUserManagement = useFeaturePermission(FeaturePermission.USER_MANAGEMENT);
  const hasWorkflows = useFeaturePermission(FeaturePermission.WORKFLOWS);

  if (!route) return true; // No route specified, assume accessible

  // Route-based permission checking
  // Unified route→permission map (both branches' routes, deduplicated).
  if (route.startsWith('/batch-jobs') || route.startsWith('/rules')) return hasTech;
  if (route.startsWith('/bundles')) return hasDispatching;
  if (route.startsWith('/certifications') || route.startsWith('/use-cases') || route.startsWith('/scenario-types'))
    return hasDriverCertifications;
  if (route.startsWith('/change-tracking')) return hasEventLogs;
  if (route.startsWith('/chat')) return hasDispatchingChat;
  if (route.startsWith('/confirmations')) return hasDispatching;
  if (route.startsWith('/customers')) return hasCustomer;
  if (route.startsWith('/dashboard')) return hasDispatching;
  if (route.startsWith('/driver-pay')) return hasDriverPay;
  if (route.startsWith('/driver-schedules')) return hasDriver;
  if (route.startsWith('/drivers')) return hasDriver;
  if (route.startsWith('/eventlogs')) return hasEventLogs;
  if (route.startsWith('/gps-shadow')) return hasDispatching;
  if (route.startsWith('/hangtags')) return hasHangtags;
  // Learning Center: the library (/learning-modules) is open to all (falls through to the
  // default `true`); only the authoring surface is gated by the learning-module feature.
  if (route.startsWith('/learning-modules/manage')) return hasLearningModule;
  if (route.startsWith('/locations') || route.startsWith('/lanes')) return hasDispatchingGeography;
  if (route.startsWith('/orders')) return hasDispatching;
  if (route.startsWith('/moves')) return hasDispatching;
  if (route.startsWith('/offers')) return hasDispatching;
  if (route.startsWith('/organizations')) return hasCustomer;
  if (route.startsWith('/outcomes')) return hasOutcomes;
  if (route.startsWith('/pipeline')) return hasDriverOnboarding;
  if (route.startsWith('/planning')) return hasDispatching;
  if (route.startsWith('/plates')) return hasDriverEquipmentManagement;
  if (route.startsWith('/programs')) return hasCustomer;
  if (route.startsWith('/promos')) return hasPromos;
  if (route.startsWith('/region-pipelines')) return hasDispatchingRegions && hasDriverOnboarding;
  if (route.startsWith('/regions')) return hasDispatchingRegions;
  if (route.startsWith('/risk-assessment')) return hasRiskAssessment;
  if (route.startsWith('/portal-users')) return hasCustomer;
  if (route.startsWith('/users')) return hasUserManagement;
  if (route.startsWith('/workflows')) return hasWorkflows;

  // Finance routes
  if (
    route.startsWith('/accessorial-report') ||
    route.startsWith('/active-time') ||
    route.startsWith('/ar-report') ||
    route.startsWith('/billing-templates') ||
    route.startsWith('/algorithms') ||
    route.startsWith('/disputed-moves') ||
    route.startsWith('/insurance') ||
    route.startsWith('/invoices') ||
    route.startsWith('/products') ||
    route.startsWith('/zero-dollar-moves')
  )
    return hasFinance;

  // Default to accessible for routes without specific permissions (timeline, unassigned, profile, etc.)
  return true;
};

interface NavProps {
  condensed: boolean;
  handleNavToggle: () => void;
}

const Nav = ({ condensed, handleNavToggle }: NavProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, { condensed });
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const [pathname, setPathname] = React.useState(location?.pathname);
  const totalUnreadCount = useUnreadCount();
  const [search, setSearch] = React.useState('');
  const [searchAnchorEl, setSearchAnchorEl] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (location?.pathname) setPathname(location?.pathname);
  }, [location]);

  const handleClickLogo = () => navigate('/');

  const handleLogout = async () => {
    const authService = (await import('@features/auth/services/authService')).authService;
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Helper function to add permission status to navigation items
  const addPermissionStatus = (items: any[]): any[] => {
    return items.map(item => {
      if (item.type === 'fold') {
        // For fold items, add permission status to subItems
        const updatedSubItems = item.subItems
          ? item.subItems.map((subItem: any) => ({
              ...subItem,
              hasPermission: useRoutePermission(subItem.route),
            }))
          : [];

        // Check if the fold has any accessible subItems
        const hasAnyAccessibleSubItems = updatedSubItems.some((subItem: any) => subItem.hasPermission);

        return {
          ...item,
          subItems: updatedSubItems,
          hasPermission: hasAnyAccessibleSubItems, // Fold is enabled if any subItem is accessible
        };
      } else {
        // For regular items, add permission status
        return {
          ...item,
          hasPermission: useRoutePermission(item.route),
        };
      }
    });
  };

  const unfilteredNavSections = [
    {
      title: 'Operations',
      items: [
        { type: 'item', label: 'Dashboard', icon: <BiSolidDashboard size={20} />, route: '/dashboard' },
        { type: 'item', label: 'Timeline', icon: <BiSolidTimeFive size={20} />, route: '/timeline' },
        {
          type: 'fold',
          label: 'Orders',
          icon: <BiSolidCar size={20} />,
          subItems: [
            { label: 'All orders', icon: <BiSolidReceiptFinance size={20} />, route: '/orders' },
            { label: 'Unassigned moves', icon: <BiSolidCar size={20} />, route: '/unassigned' },
          ],
        },
        { type: 'item', label: 'Chat', icon: <BiChat size={20} />, route: '/chat' },
        { type: 'item', label: 'Planning', icon: <BiSolidAddToQueue size={20} />, route: '/planning' },
        { type: 'item', label: 'Confirmations', icon: <BiSolidCheckCircle size={20} />, route: '/confirmations' },
        { type: 'item', label: 'Outcomes', icon: <BiSolidErrorCircle size={20} />, route: '/outcomes' },
        // { type: 'item', label: 'Risk Assessment', icon: <BiSolidShield size={20} />, route: '/risk-assessment' },
      ],
    },
    {
      title: 'Data & Management',
      items: [
        { type: 'item', label: 'Organizations', icon: <BiSolidBuildingHouse size={20} />, route: '/organizations' },
        { type: 'item', label: 'Programs', icon: <BiSolidCategory size={20} />, route: '/programs' },
        // Customers are reached by drilling into an organization (Organizations →
        // org → Customers tab → customer), so there's no top-level Customers menu
        // item. The /customers routes still exist for direct links + redirects.
        {
          type: 'fold',
          label: 'Drivers',
          icon: <BiSolidIdCard size={20} />,
          subItems: [
            { type: 'item', label: 'Drivers', icon: <BiSolidIdCard size={20} />, route: '/drivers' },
            { type: 'item', label: 'Schedules', icon: <BiCalendar size={20} />, route: '/driver-schedules' },
            { type: 'item', label: 'Pipeline', icon: <BiNetworkChart size={20} />, route: '/pipeline' },
          ],
        },
        {
          type: 'fold',
          label: 'Users',
          icon: <BiGroup size={20} />,
          subItems: [
            { type: 'item', label: 'Admin Users', icon: <BiSolidUser size={20} />, route: '/users' },
            { type: 'item', label: 'Portal Users', icon: <BiSolidUser size={20} />, route: '/portal-users' },
          ],
        },
        { type: 'item', label: 'Hangtags', icon: <BiSolidPurchaseTag size={20} />, route: '/hangtags' },
        { type: 'item', label: 'Plates', icon: <BiHash size={20} />, route: '/plates' },
        {
          type: 'fold',
          label: 'Geography',
          icon: <BiWorld size={20} />,
          subItems: [
            { type: 'item', label: 'Locations', icon: <BiSolidMapPin size={20} />, route: '/locations' },
            { type: 'item', label: 'Lanes', icon: <BiSolidDirectionRight size={20} />, route: '/lanes' },
            { type: 'item', label: 'Regions', icon: <BiSolidVector size={20} />, route: '/regions' },
            // { type: 'item', label: 'Region Pipelines', icon: <BiNetworkChart size={20} />, route: '/region-pipelines' },
          ],
        },
        { type: 'item', label: 'Workflows', icon: <BiGitBranch size={20} />, route: '/workflows' },
        { type: 'item', label: 'Settings', icon: <BiSolidCog size={20} />, route: '/settings' },
      ],
    },
    {
      title: 'Finance',
      items: [
        {
          type: 'fold',
          label: 'Accounts Receivable',
          icon: <BiSolidDollarCircle size={20} />,
          subItems: [
            { type: 'item', label: 'Invoices', icon: <BiSolidReceiptFinance size={20} />, route: '/invoices' },
            {
              type: 'item',
              label: 'Billing Templates',
              icon: <BiSolidCopyAlt size={20} />,
              route: '/billing-templates',
            },
            // { type: 'item', label: 'Disputed Moves', icon: <BiSolidErrorCircle size={20} />, route: '/disputed-moves' },
            // {
            //   type: 'item',
            //   label: 'Zero Dollar Moves',
            //   icon: <BiSolidCommentX size={20} />,
            //   route: '/zero-dollar-moves',
            // },
            // {
            //   type: 'item',
            //   label: 'AR Report (Moves)',
            //   icon: <BiSolidFileFind size={20} />,
            //   route: '/ar-report/moves',
            // },
            // {
            //   type: 'item',
            //   label: 'AR Report (Payments)',
            //   icon: <BiSolidFileFind size={20} />,
            //   route: '/ar-report/payments',
            // },
            // {
            //   type: 'item',
            //   label: 'Accessorial Report',
            //   icon: <BiSolidFileFind size={20} />,
            //   route: '/accessorial-report',
            // },
            { type: 'item', label: 'Products', icon: <BiSolidGridAlt size={20} />, route: '/products' },
          ],
        },
        {
          type: 'fold',
          label: 'Accounts Payable',
          icon: <BiSolidCheckShield size={20} />,
          subItems: [
            { type: 'item', label: 'Promotions', icon: <BiSolidPurchaseTag size={20} />, route: '/promos/manage' },
            // { type: 'item', label: 'Driver Pay', icon: <BiSolidWallet size={20} />, route: '/driver-pay' },
            // {
            //   type: 'item',
            //   label: 'Driver Payout History',
            //   icon: <BiSolidFileFind size={20} />,
            //   route: '/driver-payouts',
            // },
            // { type: 'item', label: 'Active Time', icon: <BiSolidTimeFive size={20} />, route: '/active-time' },
          ],
        },
        { type: 'item', label: 'Insurance', icon: <BiSolidShield size={20} />, route: '/insurance' },
      ],
    },
    {
      title: 'Core',
      items: [
        {
          type: 'item',
          label: 'Certifications',
          icon: <WorkspacePremiumIcon sx={{ fontSize: 20 }} />,
          route: '/certifications',
        },
        {
          // Single entry for everyone. Authoring is reached from an admin-only button on
          // the Learning Center page itself, not the sidebar.
          type: 'item',
          label: 'Learning Center',
          icon: <BiSolidBookContent size={20} />,
          route: '/learning-modules',
        },
      ],
    },
    {
      title: 'Extras',
      items: [
        {
          type: 'fold',
          label: 'Utilities',
          icon: <BiSolidWrench size={20} />,
          subItems: [
            { type: 'item', label: 'Algorithms', icon: <span>Σ</span>, route: '/algorithms' },
            { type: 'item', label: 'Offers', icon: <BiSolidDollarCircle size={20} />, route: '/offers' },
            { type: 'item', label: 'Bundles', icon: <BiSolidCategory size={20} />, route: '/bundles' },
            { type: 'item', label: 'Chat Admin', icon: <BiCog size={20} />, route: '/chat/admin' },
            { type: 'item', label: 'Event Logs', icon: <BiSolidFileFind size={20} />, route: '/eventlogs' },
            { type: 'item', label: 'GPS Shadow Review', icon: <BiSolidCompass size={20} />, route: '/gps-shadow' },
            { type: 'item', label: 'Change Tracking', icon: <BiGitBranch size={20} />, route: '/change-tracking' },

            //         { type: 'item', label: 'Batch Jobs', icon: <BiSolidTimer size={20} />, route: '/batch-jobs' },
            //         { type: 'item', label: 'FAQ Manager', icon: <BiSolidCommentDetail size={20} />, route: '/faq-manager' },
            //         { type: 'item', label: 'GPS Viewer', icon: <BiSolidCompass size={20} />, route: '/gps-viewer' },
            //         {
            //           type: 'item',
            //           label: 'Missing Region Locations',
            //           icon: <BiSolidMapAlt size={20} />,
            //           route: '/missing-region-locations',
            //         },
            //         {
            //           type: 'item',
            //           label: 'Move Calculator',
            //           icon: <BiSolidCalculator size={20} />,
            //           route: '/move-calculator',
            //         },
            //         { type: 'item', label: 'Quicklinks', icon: <BiLink size={20} />, route: '/quicklinks' },
            //         { type: 'item', label: 'Rules', icon: <BiSolidBookContent size={20} />, route: '/rules' },
          ],
        },
      ],
    },
  ];

  // Apply permission status to navigation sections
  const permittedNavSections = unfilteredNavSections.map(section => ({
    ...section,
    items: addPermissionStatus(section.items),
  }));

  // Filter by search query
  const navSections = search
    ? permittedNavSections
        .map(section => ({
          ...section,
          items: section.items.filter((item: any) => {
            if (item.type === 'fold') {
              // Keep fold if its label or any sub-item label matches
              const foldMatches = item.label.toLowerCase().includes(search.toLowerCase());
              const subMatches = (item.subItems || []).some((si: any) =>
                si.label.toLowerCase().includes(search.toLowerCase())
              );
              return foldMatches || subMatches;
            }
            return item.label.toLowerCase().includes(search.toLowerCase());
          }),
        }))
        .filter(section => section.items.length > 0)
    : permittedNavSections;

  return (
    <>
      <nav className={cls.nav}>
        <div className={cls.navBody}>
          {/* Sticky header: logo + search */}
          <div className={cls.navStickyHeader}>
            <div className={cls.navTitle} onClick={handleClickLogo} style={{ cursor: 'pointer' }}>
              <div className={cls.navTitleLogoBox}>
                <img className={cls.navTitleLogoImg1} alt='Admin Portal Logo' src={hopdriveLogo} />
                <img className={cls.navTitleLogoImg2} alt='Admin Portal Condensed Logo' src={hopdriveLogoCondensed} />
              </div>
            </div>

            {/* Search */}
            {condensed ? (
              <div style={{ padding: '0 6px', display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    borderRadius: 6,
                    padding: '4px 0',
                    width: '100%',
                  }}
                >
                  <IconButton
                    size='small'
                    onClick={e => setSearchAnchorEl(e.currentTarget)}
                    sx={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </div>
                <Popover
                  open={Boolean(searchAnchorEl)}
                  anchorEl={searchAnchorEl}
                  onClose={() => setSearchAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        ml: 1,
                        p: 1.5,
                        bgcolor: theme.palette.nav?.sidebar,
                        borderRadius: 1,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      width: 220,
                    }}
                  >
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18, mr: 1 }} />
                    <InputBase
                      autoFocus
                      placeholder='Search…'
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      sx={{
                        color: '#fff',
                        fontSize: 13,
                        '& ::placeholder': { color: 'rgba(255,255,255,0.4)' },
                      }}
                    />
                  </Box>
                </Popover>
              </div>
            ) : (
              <div style={{ padding: '0 12px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    borderRadius: 6,
                    padding: '4px 12px',
                  }}
                >
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18, mr: 1 }} />
                  <InputBase
                    placeholder='Search…'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{
                      color: '#fff',
                      fontSize: 13,
                      flex: 1,
                      '& ::placeholder': { color: 'rgba(255,255,255,0.4)' },
                    }}
                  />
                </div>
              </div>
            )}

            <div className={cls.navStickyHeaderFade} />
          </div>

          {navSections.map((section, sectionIndex) => (
            <React.Fragment key={section.title}>
              <div className={cls.navList}>
                <Typography className={cls.navListTxt}>{section.title}</Typography>
                {section.items.map((item: any) => {
                  if (item.type === 'fold') {
                    return (
                      <NavFold
                        key={item.label}
                        condensed={condensed}
                        label={item.label}
                        icon={React.cloneElement(item.icon, { size: condensed ? 24 : 20 })}
                        childRoutes={(item.subItems || []).map((si: any) => si.route).filter(Boolean)}
                        currentRoute={pathname}
                      >
                        {(item.subItems || []).map((subItem: any) => (
                          <NavItem
                            key={subItem.label}
                            condensed={condensed}
                            label={subItem.label}
                            icon={React.cloneElement(subItem.icon, { size: condensed ? 24 : 20 })}
                            route={subItem.route}
                            currentRoute={pathname}
                            disabled={!subItem.hasPermission}
                            badgeCount={subItem.route === '/chat' ? totalUnreadCount : undefined}
                          />
                        ))}
                      </NavFold>
                    );
                  }
                  return (
                    <NavItem
                      key={item.label}
                      condensed={condensed}
                      label={item.label}
                      icon={React.cloneElement(item.icon, { size: condensed ? 24 : 20 })}
                      route={item.route}
                      currentRoute={pathname}
                      disabled={!item.hasPermission}
                      badgeCount={
                        item.route === '/chat' && item.hasPermission && totalUnreadCount > 0
                          ? totalUnreadCount
                          : undefined
                      }
                    />
                  );
                })}
              </div>
              {sectionIndex < navSections.length - 1 && <div className={cls.navDivider} />}
            </React.Fragment>
          ))}
        </div>

        <div className={cls.navFooter}>
          <div className={cls.navFooterFade} />
          <Typography className={cls.navFooterTxt}>
            {condensed ? `` : `Admin Portal `}
            <b className={cls.navFooterVersionTxt}>{buildIdentity()}</b>
          </Typography>
        </div>
      </nav>

      <div
        className={cls.navOverlay}
        onClick={() => {
          if (handleNavToggle) handleNavToggle();
        }}
      />
    </>
  );
};

const useStyles = (theme?: Theme, props?: { condensed?: boolean }) => {
  const styles = {
    nav: css`
      z-index: ${theme?.zIndex?.drawer};
      position: relative;
      min-width: ${props?.condensed ? `64px` : `260px`};
      max-width: ${props?.condensed ? `64px` : `260px`};
      height: 100%;
      background-color: ${theme?.palette?.nav?.sidebar};
      color: ${theme?.palette?.text?.contrastPrimary};
      transition: all 0.2s ease-in-out;
      display: flex;
      flex-direction: column;
      ${theme?.breakpoints?.down('lg')} {
        position: fixed;
        display: ${props?.condensed ? `none` : `flex`};
        top: 0;
        left: 0;
      }
    `,
    navOverlay: css`
      z-index: ${theme?.zIndex?.drawer && typeof theme.zIndex.drawer === 'number' ? theme.zIndex.drawer - 1 : 'auto'};
      position: fixed;
      display: none;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.5);
      /* Overlay disabled - darkening content on narrow viewports was unwanted */
    `,
    navBody: css`
      position: relative;
      overflow-y: auto;
      overflow-x: hidden;
      flex-grow: 1;
      padding-bottom: 0;
      &::-webkit-scrollbar {
        width: ${props?.condensed ? '0px' : '6px'};
      }
      &::-webkit-scrollbar-thumb {
        background-color: ${props?.condensed ? 'transparent' : theme?.palette?.action?.contrastHover};
        border-radius: 3px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
    `,
    navStickyHeader: css`
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: ${theme?.palette?.nav?.sidebar};
    `,
    navStickyHeaderFade: css`
      height: 16px;
      background: linear-gradient(to bottom, ${theme?.palette?.nav?.sidebar}, transparent);
      pointer-events: none;
    `,
    navTitle: css`
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 64px;
      min-height: 64px;
      padding: ${props?.condensed ? `0` : `0 16px`};
    `,
    navTitleLogoBox: css`
      margin-top: ${props?.condensed ? `2px` : `6px`};
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    navTitleLogoImg1: css`
      display: ${props?.condensed ? `none` : `block`};
      width: 180px;
      max-height: 30px;
      cursor: pointer;
    `,
    navTitleLogoImg2: css`
      display: ${props?.condensed ? `block` : `none`};
      width: 48px;
      max-height: 48px;
      cursor: pointer;
    `,
    navList: css`
      position: relative;
      padding: ${theme?.spacing(2)} ${props?.condensed ? theme?.spacing(0.75) : theme?.spacing(1.25)};
      display: flex;
      flex-direction: column;
      align-items: ${props?.condensed ? 'center' : 'stretch'};
    `,
    navListTxt: css`
      display: ${props?.condensed ? `none` : `block`};
      margin-bottom: 8px !important;
      margin-left: ${props?.condensed ? '0px' : '6px'} !important;
      text-transform: uppercase;
      font-size: 10px !important;
      font-weight: 700 !important;
      color: rgba(255, 255, 255, 0.45);
      text-align: ${props?.condensed ? 'center' : 'left'};
    `,
    navDivider: css`
      height: 1px;
      background-color: ${theme?.palette?.dividerContrast ?? 'rgba(255,255,255,0.12)'};
      margin: 8px ${props?.condensed ? '6px' : '16px'};
    `,
    navFooter: css`
      position: relative;
      white-space: nowrap;
      width: 100%;
      min-height: 40px;
      padding: 0;
      border-top: 1px solid ${theme?.palette?.dividerContrast ?? 'rgba(255,255,255,0.12)'};
      background-color: ${theme?.palette?.nav?.sidebar};
      color: ${theme?.palette?.text?.contrastDisabled};
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
    `,
    navFooterFade: css`
      position: absolute;
      top: -16px;
      left: 0;
      width: 100%;
      height: 16px;
      background: linear-gradient(to bottom, transparent, ${theme?.palette?.nav?.sidebar});
    `,
    navFooterTxt: css`
      line-height: 40px !important;
      text-align: center;
      font-size: 10px !important;
      font-weight: 500 !important;
      width: 100%;
    `,
    navFooterVersionTxt: css`
      line-height: inherit !important;
      font-weight: 900 !important;
    `,
  };
  return styles;
};

export default Nav;
